/**
 * FERP Portal - Canonical Actor Resolution Hook
 *
 * Single source of truth for resolving the current user's backend actor.
 * Replaces 7 duplicate inline GET_MY_ACTOR queries across the codebase.
 *
 * DRR-061: Auth context → getMyActor backend query → Actor primitive.
 */

import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useTenantId } from '@/auth/AuthContext';

const GET_MY_ACTOR = gql`
  query GetMyActor {
    getMyActor {
      id
      name
      teamId
    }
  }
`;

export interface MyActorInfo {
  /** Backend actor ID (e.g., 17_2) */
  actorId: string | null;
  /** Actor display name */
  actorName: string;
  /** Actor's team ID (if assigned) */
  teamId: string | null;
  /** Whether actor resolution is loading */
  loading: boolean;
}

/**
 * Resolve the current user's backend actor via getMyActor query.
 *
 * Returns the actor ID that matches task.assignedActorId,
 * notification.recipientActorIds, and other cross-entity references.
 *
 * This is the CANONICAL hook — do not define inline GET_MY_ACTOR queries.
 */
export function useMyActor(): MyActorInfo {
  const tenantId = useTenantId();
  const { data, loading } = useQuery<{
    getMyActor: { id: string; name: string; teamId?: string } | null;
  }>(GET_MY_ACTOR, { skip: !tenantId });

  return {
    actorId: data?.getMyActor?.id ?? null,
    actorName: data?.getMyActor?.name ?? '',
    teamId: data?.getMyActor?.teamId ?? null,
    loading,
  };
}
